import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  IconButton,
  Typography,
  makeStyles,
  withStyles,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import { getListValues } from "../../../../API/List";
import { getEntity } from "./../../../../API/Entity";
import {
  getClientUsers,
  createDocumentRequest,
  getAssociationsForDocumentRequest,
} from "../../../../API/Entity/DocumentRequest";
import { getAttributeValue } from "../../../../Utilities/Functions";
import LoadingIndicator from "../../../../Component/LoadingIndicator";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import getSteps from "./steps";

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
  },
}))(MuiDialogContent);

const CreateDocumentRequest = (props) => {
  const { open, onClose, entity, reloadEntity, documentRequests } = props;
  const classes = useStyles();

  const [isDialogOpen, setIsDialogOpen] = useState(open);

  const [steps, setSteps] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [skippedSteps, setSkippedSteps] = useState({});

  const [clientUsers, setClientusers] = useState([]);
  const [lastClientUser, setLastClientUser] = useState();
  const [associatedEntities, setAssociatedEntities] = useState([]);
  const [listOptions, setListOptions] = useState();

  const [request, setRequest] = useState({ clientEntity: entity });

  const [isLoading, setIsLoading] = useState(false);

  const { showErrorMessage, showMessage } = useContext(ModalDialogContext);
  const { t } = useTranslation();

  const requestTypes = [
    {
      value: "1",
      label: t(
        "labels.record.documentRequest.steps.requestTypeStep.genericRequest",
      ),
      helpText: t("messages.record.documentRequest.genericRequestHelp"),
    },
    {
      value: "2",
      label: t(
        "labels.record.documentRequest.steps.requestTypeStep.specificRequest",
      ),
      helpText: t("messages.record.documentRequest.specificRequestHelp"),
    },
  ];

  const { id: entityId } = entity || {};

  useEffect(() => {
    const loadClientUsers = async () => {
      try {
        const result = await getClientUsers(entityId);
        const { data = [] } = result || {};
        setClientusers(
          data.map((u) => ({ username: u.user_name, userEmail: u.user_email })),
        );
      } catch (error) {
        showErrorMessage({ error });
      }
    };

    const getListOptions = async (listId, listType) => {
      const listValues = [];
      try {
        const result = await getListValues({
          listId,
          listName: null,
          listType,
        });
        const { data = [] } = result;
        for (let lv of data) {
          if (listType === "RL" || listType === "EL") {
            listValues.push({
              value: lv.code,
              label: lv.value,
              equivalent: lv.equivalent,
            });
          } else {
            listValues.push({ value: lv.code, label: lv.value });
          }
        }
      } catch (e) {
        console.log("error in fetching list " + e);
      }
      return listValues;
    };

    const loadListOptions = async () => {
      try {
        ["1", "3", "5", "6", "21", "27"].map(async (lid) => {
          const vals = await getListOptions(lid, null);
          setListOptions((los) => {
            return { ...los, [lid]: vals };
          });
        });
        ["CL", "RL", "EL"].map(async (dt) => {
          const vals = await getListOptions(null, dt);
          setListOptions((los) => {
            return { ...los, [dt]: vals };
          });
        });
      } catch (error) {
        showErrorMessage({ error });
      }
    };

    const loadAssociations = async () => {
      try {
        setIsLoading(true);
        const result = await getAssociationsForDocumentRequest(entityId);
        const { data } = result || {};
        setAssociatedEntities(data);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };

    if (open) {
      loadAssociations();
      loadClientUsers();
      loadListOptions();
    }
  }, [open]);

  useEffect(() => {
    setSteps(getSteps(request, entity, associatedEntities));
  }, [request, entity, associatedEntities]);

  const renderSteps = () => {
    return (
      <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
        {steps.map((step, index) => (
          <Step key={index}>
            <StepLabel
              StepIconComponent={getStepIcon}
              onClick={handleStep(step, index)}
              className={classes.stepLabel}
              completed={completedSteps[index]}
              disabled={step.isDisabled(
                request,
                steps,
                index,
                completedSteps,
                skippedSteps,
              )}
            >
              {step.getLabel(t)}
            </StepLabel>
            <StepContent>
              {step.getComponent({
                stepIndex: index,
                request,
                entity,
                requestTypes,
                clientUsers,
                lastClientUser,
                associatedEntities,
                listOptions,
                reload,
                documentRequests,
                canSkip: step.canSkip(request),
                onCancel: handleClose,
                onNext: handleNext,
                onBack: handleBack,
                onSkip: handleSkip,
                onSubmit: handleSubmit,
              })}
            </StepContent>
          </Step>
        ))}
      </Stepper>
    );
  };

  const getStepIcon = (props) => {
    const { active, completed, icon } = props;
    let className = classes.stepIcon;
    if (active) {
      className = `${className} ${classes.stepIconActive}`;
    }
    if (completed) {
      className = `${className} ${classes.stepIconComplete}`;
    }
    if (
      steps[icon - 1] &&
      !steps[icon - 1].isDisabled(
        request,
        steps,
        icon - 1,
        completedSteps,
        skippedSteps,
      )
    ) {
      className = `${className} ${classes.stepIconEnabled}`;
    }
    return (
      <div className={className}>
        {completed ? (
          <CheckIcon fontSize="small" />
        ) : active ? (
          <div className={classes.circle} />
        ) : (
          ""
        )}
      </div>
    );
  };

  const handleStep = (step, stepIndex) => () => {
    if (
      !step.isDisabled(request, steps, stepIndex, completedSteps, skippedSteps)
    ) {
      setActiveStep(stepIndex);
    }
  };

  const reload = async (entityId) => {
    if (entity.id === entityId) {
      reloadEntity(true, true);
    } else {
      const result = await getEntity({ entityId });
      const { data = {} } = result;
      setAssociatedEntities(
        associatedEntities.map((ae) =>
          ae.entity.id === entityId ? { ...ae, entity: data } : ae,
        ),
      );
    }
  };

  const handleNext = (isComplete, params) => {
    setRequest(steps[activeStep].updateRequest(request, params));
    if (params.clientUser) {
      setLastClientUser(params.clientUser);
      const cu = clientUsers.find(
        (u) =>
          u.username === params.clientUser.username &&
          u.userEmail === params.clientUser.userEmail,
      );
      if (!cu) {
        clientUsers.push(params.clientUser);
      }
    }
    setCompletedSteps({ ...completedSteps, [activeStep]: isComplete });
    setActiveStep(getNextStep());
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (skippedSteps[activeStep] !== true) {
      setSkippedSteps({ ...skippedSteps, [activeStep]: true });
    }
    setActiveStep(getNextStep());
  };

  const getNextStep = (current) => {
    if (
      steps[activeStep + 1] &&
      steps[activeStep + 1].isDisabled(
        request,
        steps,
        activeStep,
        completedSteps,
        skippedSteps,
      )
    ) {
      for (let si = 0; si < activeStep; si++) {
        if (!steps[si].canSkip(request) && !completedSteps[si]) {
          return si;
        }
      }
    }
    return activeStep + 1;
  };

  const handleClose = (status) => {
    setIsDialogOpen(false);
    if (onClose) {
      onClose(status);
    }
  };

  const cancel = () => {
    handleClose("cancel");
  };

  const handleSubmit = async (params) => {
    setCompletedSteps({ ...completedSteps, [activeStep]: true });
    const newRequest = steps[activeStep].updateRequest(request, params);
    setRequest(newRequest);
    const { challengeResponse } = params;
    try {
      setIsLoading(true);
      await createDocumentRequest(
        entityId,
        challengeResponse,
        createRequest(newRequest),
      );
      showMessage({
        message: t("messages.record.documentRequest.submitted"),
      });
      handleClose();
    } catch (error) {
      showErrorMessage({ error });
    }
    setIsLoading(false);
  };

  const createRequest = (newRequest) => {
    const { requestType, clientUser, userMessage, records = [] } = newRequest;
    const recs = records.map((r) => {
      return { ...r, entity: mapEntity(r.entity) };
    });
    return { requestType, clientUser, userMessage, records: recs };
  };

  const mapEntity = (ent) => {
    const {
      id,
      urn,
      entity_category: entityCategory,
      entity_subcategory: entitySubcategory,
      attributes = [],
      address,
    } = ent;
    return {
      id,
      urn,
      entityCategory,
      entitySubcategory,
      attributes: mapAttributes(attributes),
      address: mapAddress(address),
    };
  };

  const mapAttributes = (attributes) => {
    return [...attributes].map((a) => ({
      id: a.id,
      value: `${getAttributeValue(a)}`,
    }));
  };

  const mapAddress = (address) => {
    if (address) {
      return {
        id: address.id,
        addressLine1: address.address_line_1,
        addressLine2: address.address_line_2,
        addressLine3: address.address_line_3,
        addressLine4: address.address_line_4,
        city: address.city,
        county: address.county,
        stateProvince: address.state_province,
        postcode: address.postcode,
        countryCode: address.country_code,
      };
    }
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  return (
    <Dialog
      disableBackdropClick
      scroll={"paper"}
      onClose={cancel}
      aria-labelledby="add-document-modal"
      fullWidth
      open={isDialogOpen}
      maxWidth="lg"
    >
      <DialogTitle id="add-document-modal" onClose={cancel}>
        <Typography className={classes.panelTitle}>
          {t("labels.record.documentRequest.newRequest")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? renderLoadingIndicator() : renderSteps()}
      </DialogContent>
    </Dialog>
  );
};

export default CreateDocumentRequest;
